import type { ReactNode } from 'react';
import { Combobox } from '@base-ui/react/combobox';
import { cn } from '../../lib/cn.ts';
import { Spinner } from '../spinner/index.ts';
import type { SearchSelectLabels } from './search-select-labels.ts';
import type { AsyncOptionsStatus } from './useAsyncOptions.ts';

interface SearchSelectPopupProps<T> {
  status: AsyncOptionsStatus;
  /** Too short a query is not searched, and the popup says to keep typing. */
  queryTooShort: boolean;
  getKey: (item: T) => string;
  getLabel: (item: T) => string;
  renderOption?: (item: T) => ReactNode;
  labels: SearchSelectLabels;
}

/** The list under the field, which also says when it is loading, empty or failed. */
export function SearchSelectPopup<T>({
  status,
  queryTooShort,
  getKey,
  getLabel,
  renderOption,
  labels,
}: SearchSelectPopupProps<T>) {
  function statusMessage(): ReactNode {
    if (status === 'loading') {
      return (
        <>
          <Spinner className='size-3' />
          {labels.loading}
        </>
      );
    }
    if (status === 'error') return labels.error;
    if (queryTooShort) return labels.hint;
    return null;
  }

  return (
    <Combobox.Portal>
      <Combobox.Positioner side='bottom' align='start' sideOffset={6} className='z-50'>
        {/*
          No padding on the popup and no rounding on the rows, the same as the
          filter menus and the static combobox: a highlighted row is a filled
          row, and an inset pill of fill leaves white wedges at the edges that
          read as a rendering fault.
        */}
        <Combobox.Popup
          data-slot='search-select-list'
          aria-busy={status === 'loading' || undefined}
          className={cn(
            'max-h-56 w-(--anchor-width) overflow-y-auto overscroll-contain',
            'rounded-md border border-line bg-surface shadow-menu outline-none animate-pop',
          )}
        >
          {/*
            Both of these stay mounted whatever they have to say — they
            announce their changes, and a message that is announced by
            appearing is a message half of screen readers never hear.
          */}
          <Combobox.Status
            data-slot='search-select-status'
            className='flex items-center gap-2 px-3.5 py-2.25 text-sm font-semibold text-ink-muted empty:hidden'
          >
            {statusMessage()}
          </Combobox.Status>
          <Combobox.Empty className='px-3.5 py-2.25 text-sm font-semibold text-ink-muted empty:hidden'>
            {status === 'ready' ? labels.empty : null}
          </Combobox.Empty>
          <Combobox.List>
            {(item: T) => (
              <Combobox.Item
                key={getKey(item)}
                value={item}
                data-slot='search-select-option'
                className={cn(
                  'px-3.5 py-2.25 text-sm font-semibold text-ink outline-none transition-colors',
                  'data-highlighted:bg-fill data-selected:bg-fill',
                  renderOption === undefined && 'truncate',
                )}
              >
                {renderOption ? renderOption(item) : getLabel(item)}
              </Combobox.Item>
            )}
          </Combobox.List>
        </Combobox.Popup>
      </Combobox.Positioner>
    </Combobox.Portal>
  );
}
